import { Form } from "antd";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getFormError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import useProductApi from "./useProductApi";

interface UseProductModalParams {
  productId?: ID;
  onSuccess?: () => void;
}

export default function useProductModal({ productId, onSuccess }: UseProductModalParams = {}) {
  const urlParams: RouteParams = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [isOpen, setIsOpen] = useState(false);

  const { createUpdateProduct } = useProductApi(productId);
  const { notificationSuccessfullySaved, notificationError } = useNotification();

  const showModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    form.resetFields();
  }, [form]);

  const handleOk = useCallback(() => {
    form.submit();
  }, [form]);

  const buildProductInput = useCallback(
    (values: any, isUpdate: boolean) => {
      const prices = values?.prices?.map((item: any) => ({
        currency: item?.currency,
        buyPrice: item?.buyPriceTotal,
        priceNet: item?.priceNet,
      }));

      const translations = values?.translations?.map((item: any) => ({
        language: item?.language,
        name: item?.name,
        description: item?.description,
      }));

      const updateId = isUpdate ? { id: productId } : { prices };

      return {
        category: values?.category,
        operationalCategory: values?.operationalCategory,
        reference: values?.reference,
        translations,
        vat: values?.vat?.value,
        counterpartCode: values?.counterpartCode?.value,
        productType: values?.productType?.value,
        unitMeasure: values?.unitMeasure?.value,
        ...updateId,
      };
    },
    [productId],
  );

  const onFinish = useCallback(
    async (values: any) => {
      const isUpdate = !!productId;
      const input = buildProductInput(values, isUpdate);

      const response = await createUpdateProduct({
        variables: { input } as unknown as NonNullable<
          Parameters<typeof createUpdateProduct>[0]
        >["variables"],
      });

      const data = response?.data as Record<string, { errors?: any[] }> | undefined;
      const errors = isUpdate ? data?.updateProduct?.errors : data?.createProduct?.errors;

      if (errors && errors.length > 0) {
        const fieldsData = getFormError(errors, values);
        notificationError(errors);
        form.setFields(fieldsData);
      } else {
        onSuccess?.();
        form.resetFields();
        setIsOpen(false);
        notificationSuccessfullySaved(t("Product"));
      }
    },
    [
      productId,
      buildProductInput,
      createUpdateProduct,
      notificationError,
      notificationSuccessfullySaved,
      form,
      t,
      onSuccess,
    ],
  );

  return {
    form,
    isOpen,
    showModal,
    handleOk,
    handleCancel,
    onFinish,
    urlParams,
  };
}
